import { useLocation } from 'react-router-dom';

export type FromRouteState = { fromRoute?: string };

/**
 * Reads router state, returning `fromRoute` separately from the rest of the state
 * (`locationState`). `locationState` is `Partial<T>` because router state is
 * untrusted — it may be absent or missing fields, so callers must handle gaps.
 * Pass `fallbackRoute` to receive `fromRoute` as a guaranteed string when none
 * was set; otherwise it may be `undefined`.
 */
export function useLocationState<T = unknown>(): {
  fromRoute: string | undefined;
  locationState: Partial<T>;
};
export function useLocationState<T = unknown>(
  fallbackRoute: string,
): {
  fromRoute: string;
  locationState: Partial<T>;
};
export function useLocationState<T = unknown>(
  fallbackRoute?: string,
): {
  fromRoute: string | undefined;
  locationState: Partial<T>;
} {
  const { state } = useLocation<Partial<T & FromRouteState>>();
  const { fromRoute, ...restState } = state ?? {};
  return {
    fromRoute: fromRoute ?? fallbackRoute,
    locationState: restState as Partial<T>,
  };
}
